import { useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { isTokenExpired, getTimeUntilTokenExpiry } from "../utils/utils";

export const useTokenSync = () => {
  const authTokens = useSelector((state) => state.auth.authTokens);
  const user = useSelector((state) => state.auth.user);

  const isCurrentTokenExpired = useCallback(() => {
    return isTokenExpired(authTokens?.access);
  }, [authTokens?.access]);

  const getTimeUntilExpiry = useCallback(() => {
    return getTimeUntilTokenExpiry(authTokens?.access);
  }, [authTokens?.access]);

  /**
   * Cross-tab session state listener.
   *
   * Reacts ONLY to login/logout transitions in localStorage, NOT to token
   * refreshes. This prevents the cascade where Tab A refreshes → Tab B detects
   * the change → Tab B also tries to refresh with the now-invalidated token
   * → Tab B logs out → Tab A detects the logout → login loop.
   *
   * | Transition              | Meaning              | Action                    |
   * |-------------------------|----------------------|---------------------------|
   * | null → valid tokens     | Login in another tab | Navigate to /welcome      |
   * | valid → null            | Logout in another tab| Navigate to /auth/login   |
   * | valid → different valid | Token refresh        | Ignore (no cascade)       |
   */
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key !== "persist:root") return;

      try {
        const newState = JSON.parse(e.newValue);
        const oldState = JSON.parse(e.oldValue || "{}");
        if (!newState.auth || !oldState.auth) return;

        const newAuth = JSON.parse(newState.auth);
        const oldAuth = JSON.parse(oldState.auth);
        const oldTokens = oldAuth.authTokens;
        const newTokens = newAuth.authTokens;

        if (!oldTokens && newTokens) {
          if (window.location.pathname !== "/welcome") {
            window.location.href = "/welcome";
          }
          return;
        }

        if (oldTokens && !newTokens) {
          if (window.location.pathname !== "/auth/login") {
            window.location.href = "/auth/login";
          }
          return;
        }
      } catch (error) {
        console.warn("[TokenSync] Error parsing storage change:", error);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  return {
    authTokens,
    user,
    isCurrentTokenExpired,
    getTimeUntilExpiry,
  };
};
